import { EventSourcePlus } from "event-source-plus";
import fileTypeChecker from "file-type-checker";
import * as SharedChatCommandSchema from "../../darvin-shared/schema/zod/agent/chat-command.schema";
import * as SharedChatResponse from "../../darvin-shared/schema/zod/agent/chat-response.schema";
import * as SharedCheckpointInfo from "../../darvin-shared/schema/zod/agent/checkpoint-info-response.schema";
import { auth } from "../config/firebase_config";
import {
  HttpAssistantIsWorkingException,
  HttpFailedToFetchException,
  HttpNotEnoughTokensException,
  HttpTimeoutException,
} from "../model/httpException";
import { ImageService } from "../services/imageService";
import { getAuthHeaders } from "../utils/authHeaders";
import { logException } from "../utils/logUtils";

interface Attachment {
  content: string;
  mimeType: string;
  filename: string;
}

export class ChatApi {
  private static readonly BASE_URL = import.meta.env.VITE_API_URL;
  private static readonly BASE_ENDPOINT = `chat`;

  private static decodeJWT(token: string): { exp: number } {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  }

  static async createNewProject(): Promise<string> {
    const headers = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetch(`${this.BASE_URL}/${this.BASE_ENDPOINT}/new`, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      logException("Chat API Error (/chat/new):", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    const responseJson = await response.json();
    return responseJson.projectId;
  }

  static async getSSE(projectId: string): Promise<EventSourcePlus> {
    const user = auth.currentUser;
    // Force token refresh if it's close to expiration
    const authToken = await user?.getIdToken();

    if (!authToken) {
      throw new Error("User not authenticated");
    }

    if (authToken) {
      const decodedToken = this.decodeJWT(authToken);
      const expirationDate = new Date(decodedToken.exp * 1000);
      console.log("Token expiration date:", expirationDate.toLocaleString());
    }

    const headers = await getAuthHeaders();

    //TODO: Below assignment to baseUrl is required because fetch is handled by `event-source-plus` package which requires a full URL, not relative one
    const baseUrl = this.BASE_URL;
    const urlStr = `${baseUrl}/${this.BASE_ENDPOINT}/stream/${projectId}`;

    const eventSource = new EventSourcePlus(urlStr, {
      credentials: "include",
      maxRetryCount: 5,
      headers,
    });

    return eventSource;
  }

  static async loadChatHistory(
    projectId: string
  ): Promise<SharedChatResponse.MessageBody[]> {
    // Create AbortController with 3-minute timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3 * 60 * 1000);

    try {
      const headers = await getAuthHeaders({});

      const response = await fetch(
        `${this.BASE_URL}/${this.BASE_ENDPOINT}/history/${projectId}`,
        {
          method: "GET",
          headers,
          credentials: "include",
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        logException(`Chat API Error (/chat/history/${projectId}):`, {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        throw new HttpFailedToFetchException();
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new HttpTimeoutException();
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async sendMessage(
    projectId: string,
    message: string,
    image?: File | null,
    model?: string
  ): Promise<void> {
    const attachments: Attachment[] = [];

    if (image) {
      const compressedImg = await ImageService.compressImageIfNeeded(image);
      const arrayBuffer = await compressedImg.arrayBuffer();
      const imageInfo = fileTypeChecker.detectFile(arrayBuffer);

      const base64 = await ImageService.convertImageToBase64(compressedImg);
      attachments.push({
        content: base64,
        mimeType:
          imageInfo?.mimeType ?? this.getMimeTypeFromFileName(image.name),
        filename: image.name,
      });
    }

    const bodyJson = JSON.stringify({
      message,
      attachments,
      model,
    });

    const headers = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetch(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/message/${projectId}`,
      {
        method: "POST",
        headers,
        credentials: "include",
        body: bodyJson,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Chat API Error (/chat/message/${projectId}):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      switch (response.status) {
        case 402:
          throw new HttpNotEnoughTokensException();
        case 409:
          throw new HttpAssistantIsWorkingException();
        default:
          throw new HttpFailedToFetchException();
      }
    }
  }

  static async sendCommand(
    projectId: string,
    chatCommand: SharedChatCommandSchema.ChatCommand
  ): Promise<void> {
    const bodyJson = JSON.stringify(chatCommand);

    const headers = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetch(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/command/${projectId}`,
      {
        method: "POST",
        headers,
        credentials: "include",
        body: bodyJson,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Chat API Error (/chat/command/${projectId}):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      switch (response.status) {
        case 402:
          throw new HttpNotEnoughTokensException();
        case 409:
          throw new HttpAssistantIsWorkingException();
        default:
          throw new HttpFailedToFetchException();
      }
    }
  }

  static async loadCheckpointInfo(
    projectId: string
  ): Promise<SharedCheckpointInfo.CheckpointInfoResponse> {
    const headers = await getAuthHeaders({});

    const response = await fetch(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/checkpoint-info/${projectId}`,
      {
        method: "GET",
        headers,
        credentials: "include",
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Chat API Error (/chat/checkpoint-info/${projectId}):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    return await response.json();
  }

  private static getMimeTypeFromFileName(fileName: string): string {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "webp":
        return "image/webp";
      default:
        return "image/png";
    }
  }
}
